import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import { FC } from 'react'
import { CemeteryAccess } from './CemeteryAccess'
import { Features } from './Features'
import { makeLabels } from '@lifedot/utils'
import { Details } from './Details'
import { replacementImage } from '@lifedot/constants/images'
import { Price } from './Price'
import { css } from '@emotion/react'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { Overview } from './Overview'
import { mq } from '@lifedot/styles/mediaQuery'
import { bestDisplayablePurchasePrices } from '@lifedot/utils'

const styles = {
  root: css({
    overflowX: 'scroll',
    '::-webkit-scrollbar': {
      height: 16
    },
    '::-webkit-scrollbar-corner': {
      background: 'transparent'
    },
    '::-webkit-scrollbar-thumb': {
      backgroundColor: palette.gray3
    },
    '::-webkit-scrollbar-track': {
      backgroundColor: palette.gray1,
      border: 'none'
    },
    table: {
      marginBottom: 8,
      width: 'auto'
    },
    p: {
      margin: 0
    }
  }),
  table: css({
    writingMode: 'vertical-lr',
    msWritingMode: 'vertical-lr',
    'th, td': {
      '> div': {
        writingMode: 'horizontal-tb',
        msWritingMode: 'horizontal-tb'
      }
    },
    'thead th': {
      backgroundColor: palette.gray1,
      fontWeight: 'normal',
      '> div': {
        margin: 'auto 0',
        width: 48
      }
    },
    td: {
      verticalAlign: 'top',
      '> div': {
        width: 192
      }
    }
  }),
  overview: css({
    minHeight: 246,
    [mq('sp')]: {
      minHeight: 236
    }
  }),
  access: css({
    minHeight: 62,
    [mq('sp')]: {
      minHeight: 52
    }
  }),
  price: css({
    minHeight: 24,
    [mq('sp')]: {
      minHeight: 20
    }
  }),
  feature: css({
    minHeight: 88,
    [mq('sp')]: {
      minHeight: 76
    }
  }),
  details: css({
    minHeight: 186,
    [mq('sp')]: {
      minHeight: 178
    }
  })
}

interface CemeteriesTableProps {
  cemeteryIds: number[]
}

export const CemeteriesTable: FC<CemeteriesTableProps> = ({ cemeteryIds }) => {
  const cemeteries = useShortCodeDataCemeteries(cemeteryIds)
  return (
    <div css={styles.root}>
      <table css={styles.table}>
        <thead>
          <tr css={typography.textXS}>
            <th>
              <div>霊園概要</div>
            </th>
            <th>
              <div>
                所在地
                <br />
                アクセス
              </div>
            </th>
            <th>
              <div>価格</div>
            </th>
            <th>
              <div>特徴</div>
            </th>
            <th>
              <div>詳細情報</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {cemeteries.map((cemetery, index) => {
            const {
              cemeteryId,
              name,
              section_types: sectionTypes,
              main_image: mainImage,
              review_summary: reviewSummary,
              city,
              prefecture,
              nearest_station: station,
              features,
              facilities,
              director_comment: directorComment,
              requestable
            } = cemetery
            const labels = makeLabels([...features, ...facilities])
            const price = bestDisplayablePurchasePrices(
              cemetery.best_price_by_category
            )
            return (
              <tr key={`${cemeteryId}_${index}`}>
                <td>
                  <div css={styles.overview}>
                    <Overview
                      cemeteryId={cemeteryId}
                      name={name}
                      labels={[...sectionTypes]}
                      img={{
                        path: mainImage?.path ?? replacementImage.path,
                        alt:
                          mainImage?.alt ??
                          (mainImage?.path ? name : replacementImage.alt)
                      }}
                      review={
                        reviewSummary.total_score !== null
                          ? {
                              score: reviewSummary.total_score,
                              count: reviewSummary.count
                            }
                          : undefined
                      }
                    />
                  </div>
                </td>
                <td>
                  <div css={styles.access}>
                    <CemeteryAccess
                      cityName={city.name}
                      prefectureName={prefecture.name}
                      station={station}
                    />
                  </div>
                </td>
                <td>
                  <div css={styles.price}>
                    <Price price={price} />
                  </div>
                </td>
                <td>
                  <div css={styles.feature}>
                    <Features labels={labels.map(({ name }) => name)} />
                  </div>
                </td>
                <td>
                  <div css={styles.details}>
                    <Details
                      directorComment={directorComment}
                      cemeteryId={cemeteryId}
                      requestable={requestable}
                    />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
